import React, { Children, memo } from 'react';
import { motion } from 'framer-motion';
import { Check, Star, BookOpen, Gift, ShieldCheck, ArrowRight, Download, Heart, Utensils, Smile, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Accordion } from '../components/ui/Accordion';
import { FloatingEmojis } from '../components/FloatingEmojis';
import { CountdownTimer } from '../components/CountdownTimer';
// Animation variants
const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function LibroDigitalPage() {
  return <div className="min-h-screen relative overflow-x-hidden font-sans" data-id="element-26">
      <FloatingEmojis data-id="element-27" />
      {/* --- HERO SECTION --- */}
      <section className="relative pt-12 pb-20 px-4 md:pt-20 md:pb-32 z-10" data-id="element-28">
        <div className="max-w-4xl mx-auto text-center" data-id="element-29">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="space-y-6" data-id="element-30">
            <h1 className="text-4xl md:text-6xl font-black leading-tight text-gray-900" data-id="element-31">
              Lo más difícil no es{' '}
              <span className="text-[#EF4444]" data-id="element-32">cocinar</span>.
              <br data-id="element-33" />
              Es pensar qué hacer{' '}
              <span className="text-[#EF4444]" data-id="element-34">todos los días</span>.
            </h1>

            <p className="text-xl md:text-2xl font-medium text-gray-700 max-w-2xl mx-auto" data-id="element-35">
              Si estás{' '}
              <span className="text-[#EF4444] font-bold" data-id="element-36">sin tiempo</span>,{' '}
              <span className="text-[#EF4444] font-bold" data-id="element-37">sin ideas</span> y
              siempre terminás haciendo lo mismo,{' '}
              <span className="text-[#10B981] font-bold" data-id="element-38">esto es para vos</span>
              .
            </p>

            <p className="text-lg md:text-xl text-gray-800 font-semibold" data-id="element-39">
              Empezá hoy a resolver el{' '}
              <span className="text-[#EF4444]" data-id="element-40">"qué cocino"</span> de todos los
              días.
            </p>

            {/* 3D Book Presentation */}
            <motion.div className="relative w-64 md:w-80 mx-auto my-12" whileHover={{
            scale: 1.05,
            rotate: -2
          }} transition={{
            type: 'spring',
            stiffness: 300
          }} data-id="element-41">
              <div className="absolute inset-0 bg-[#10B981] rounded-lg blur-2xl opacity-20 transform translate-y-4" data-id="element-42"></div>
              <img src="https://cdn.magicpatterns.com/uploads/fiixULfCbFeXEPK4fxENYi/PHOTO-2025-12-17-10-47-19-removebg-preview.webp" alt="Libro Digital ¡Mi Hijo Sí Come!" className="relative rounded-lg shadow-2xl border-4 border-white transform -rotate-2 hover:rotate-0 transition-transform duration-500 object-cover aspect-[3/4]" data-id="element-43" />
              <div className="absolute -bottom-6 -right-6 bg-white p-3 rounded-full shadow-xl border-2 border-[#FBBF24]" data-id="element-44">
                <span className="text-3xl" data-id="element-45">🥗</span>
              </div>
              <div className="absolute -top-6 -left-6 bg-white p-3 rounded-full shadow-xl border-2 border-[#EF4444]" data-id="element-46">
                <span className="text-3xl" data-id="element-47">👶</span>
              </div>
            </motion.div>

            <div className="space-y-4" data-id="element-48">
              <p className="text-gray-600 text-sm md:text-base" data-id="element-49">
                Recetas{' '}
                <span className="text-[#EF4444] font-bold" data-id="element-50">
                  fáciles, económicas
                </span>{' '}
                y{' '}
                <span className="text-[#EF4444] font-bold" data-id="element-51">
                  sin ingredientes raros
                </span>
                ,<br data-id="element-52" />
                listas para cuando no tenés{' '}
                <span className="text-[#EF4444] font-bold" data-id="element-53">tiempo</span> ni{' '}
                <span className="text-[#EF4444] font-bold" data-id="element-54">ideas</span>.
              </p>

              <Button size="xl" className="w-full md:w-auto shadow-green-500/50 shadow-2xl animate-bounce-subtle" onClick={() => window.location.href = 'https://crianzafeliz.online/cart/45751444930750:1'} data-id="element-55">
                <Download className="w-6 h-6 mr-2" data-id="element-56" />
                Descargar ahora
              </Button>

              <div className="flex flex-col items-center gap-2 text-xs text-gray-500 mt-4" data-id="element-57">
                <div className="flex items-center gap-4" data-id="element-58">
                  <span className="flex items-center" data-id="element-59">
                    <ClockIcon className="w-3 h-3 mr-1" data-id="element-60" /> 10-15 minutos
                  </span>
                  <span className="flex items-center" data-id="element-61">
                    <GiftIcon className="w-3 h-3 mr-1" data-id="element-62" /> +6 bonus incluidos
                  </span>
                </div>
                <p className="flex items-center" data-id="element-63">
                  <ShieldCheck className="w-3 h-3 mr-1" data-id="element-64" /> Acceso inmediato por
                  email
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      {/* --- PAIN POINTS SECTION --- */}
      <section className="py-16 px-4 bg-white/80 backdrop-blur-sm z-10 relative" data-id="element-65">
        <div className="max-w-3xl mx-auto" data-id="element-66">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeInUp} className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-red-100" data-id="element-67">
            <h2 className="text-2xl md:text-3xl font-bold text-center text-[#EF4444] mb-8" data-id="element-68">
              ¿Te sentís identificada con esto?
            </h2>

            <ul className="space-y-4" data-id="element-69">
              {['Sentís que cocinás siempre lo mismo y te aburre.', 'Tu hijo/a rechaza las verduras y no sabés cómo integrarlas.', 'Terminás cayendo en fideos o milanesas por falta de tiempo.', 'Te estresa pensar qué cocinar cuando llegás cansada del trabajo.', 'Querés que tu familia coma mejor pero no sabés por dónde empezar.'].map((item, i) => <motion.li key={i} className="flex items-start gap-3 text-gray-700 font-medium" initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: i * 0.1
            }} viewport={{
              once: true
            }} data-id="element-70">
                  <div className="bg-red-100 p-1 rounded-full mt-1 flex-shrink-0" data-id="element-71">
                    <span className="text-red-500 text-xs" data-id="element-72">❌</span>
                  </div>
                  {item}
                </motion.li>)}
            </ul>

            <div className="mt-8 pt-8 border-t border-gray-100 text-center font-bold text-gray-800" data-id="element-73">
              Si respondiste que SÍ al menos a una...{' '}
              <span className="text-[#F59E0B]" data-id="element-74">seguí leyendo.</span>
            </div>
          </motion.div>
        </div>
      </section>
      {/* --- BENEFITS SECTION --- */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-green-50/30 z-10 relative" data-id="element-75">
        <div className="max-w-4xl mx-auto" data-id="element-76">
          <div className="text-center mb-12" data-id="element-77">
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4" data-id="element-78">
              Con este libro vas a tener:
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-79">
            {[{
            icon: '⏱️',
            title: 'Recetas listas en 10-15 minutos',
            desc: 'Perfectas para cuando no tenés tiempo pero querés que tu familia coma rico y saludable.'
          }, {
            icon: '🎁',
            title: '+6 bonus incluidos',
            desc: 'Menú semanal, guía de viandas, snacks saludables, recetas para fiestas y más.'
          }, {
            icon: '🥗',
            title: 'Hoy solo $16.990',
            desc: 'Precio especial por tiempo limitado. Acceso inmediato por email.'
          }, {
            icon: '✅',
            title: 'Sin ingredientes raros',
            desc: 'Todo lo que necesitás lo conseguís en tu verdulería o supermercado de barrio.'
          }, {
            icon: '👨‍👩‍👧‍👦',
            title: 'Para toda la familia',
            desc: 'Recetas que le van a gustar a chicos y grandes. Adiós a cocinar dos comidas diferentes.'
          }, {
            icon: '📱',
            title: 'Acceso de por vida',
            desc: 'Descargalo en tu celular, tablet o compu. Lo tenés disponible siempre que lo necesites.'
          }].map((benefit, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100 hover:shadow-lg transition-shadow" data-id="element-80">
                <div className="flex items-start gap-4" data-id="element-81">
                  <div className="text-4xl flex-shrink-0" data-id="element-82">{benefit.icon}</div>
                  <div data-id="element-83">
                    <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-84">
                      {benefit.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed" data-id="element-85">
                      {benefit.desc}
                    </p>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* --- SOLUTION SECTION --- */}
      <section className="py-20 px-4 z-10 relative" data-id="element-86">
        <div className="max-w-5xl mx-auto" data-id="element-87">
          <div className="text-center mb-16" data-id="element-88">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4" data-id="element-89">
              Lo que va a cambiar con el{' '}
              <span className="text-[#10B981]" data-id="element-90">LIBRO DIGITAL</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" data-id="element-91">
            {[{
            icon: <Utensils className="w-8 h-8 text-[#10B981]" data-id="element-92" />,
            title: 'Variedad en tu mesa',
            desc: 'Vas a tener más de 200 opciones para no repetir siempre lo mismo.'
          }, {
            icon: <ClockIcon className="w-8 h-8 text-[#10B981]" data-id="element-93" />,
            title: 'Ahorro de tiempo',
            desc: 'Recetas pensadas para hacerse en 15-30 minutos. Ideal para madres ocupadas.'
          }, {
            icon: <Smile className="w-8 h-8 text-[#10B981]" data-id="element-94" />,
            title: 'Paz mental',
            desc: "Chau al estrés de 'qué comemos hoy'. Tenés el plan resuelto."
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.2
          }} viewport={{
            once: true
          }} className="bg-white p-8 rounded-2xl shadow-lg border-b-4 border-[#10B981] hover:-translate-y-2 transition-transform duration-300" data-id="element-95">
                <div className="bg-green-50 w-16 h-16 rounded-full flex items-center justify-center mb-6 mx-auto" data-id="element-96">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 text-center" data-id="element-97">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-center leading-relaxed" data-id="element-98">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* --- PURPLE OFFER SECTION --- */}
      <section className="py-20 px-4 bg-[#7C3AED] text-white relative overflow-hidden" data-id="element-99">
        {/* Decorative background circles */}
        <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2" data-id="element-100" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white opacity-5 rounded-full translate-x-1/3 translate-y-1/3" data-id="element-101"></div>

        <div className="max-w-6xl mx-auto relative z-10" data-id="element-102">
          <div className="text-center mb-12" data-id="element-103">
            <h2 className="text-3xl md:text-5xl font-black mb-2 tracking-tight" data-id="element-104">
              OFERTA ESPECIAL
            </h2>
            <p className="text-purple-200 uppercase tracking-widest font-semibold" data-id="element-105">
              Por tiempo limitado
            </p>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-12" data-id="element-106">
            {/* Left: Product Mockup */}
            <motion.div initial={{
            x: -50,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} className="relative" data-id="element-107">
              <div className="relative z-10 bg-white p-2 rounded-xl shadow-2xl transform rotate-3" data-id="element-108">
                <img src="https://cdn.magicpatterns.com/uploads/fiixULfCbFeXEPK4fxENYi/PHOTO-2025-12-17-10-47-19-removebg-preview.webp" alt="Pack Completo" className="rounded-lg w-64 md:w-80" data-id="element-109" />
              </div>
              {/* Badge */}
              <div className="absolute -top-6 -right-6 z-20 bg-[#FBBF24] text-gray-900 font-black rounded-full w-24 h-24 flex flex-col items-center justify-center transform rotate-12 shadow-lg border-4 border-white" data-id="element-110">
                <span className="text-xs" data-id="element-111">AHORRÁ</span>
                <span className="text-2xl" data-id="element-112">60%</span>
              </div>
            </motion.div>

            {/* Right: Offer Details */}
            <motion.div initial={{
            x: 50,
            opacity: 0
          }} whileInView={{
            x: 0,
            opacity: 1
          }} viewport={{
            once: true
          }} className="bg-white/10 backdrop-blur-md p-8 rounded-3xl border border-white/20 max-w-md w-full" data-id="element-113">
              <div className="text-center mb-6" data-id="element-114">
                <span className="text-gray-300 line-through text-lg" data-id="element-115">
                  $42.500
                </span>
                <div className="text-5xl font-black text-white mb-2" data-id="element-116">
                  $16.990
                </div>
                <div className="inline-block bg-[#10B981] text-white px-3 py-1 rounded-full text-sm font-bold" data-id="element-117">
                  Pago único - Acceso de por vida
                </div>
              </div>

              <ul className="space-y-3 mb-8 text-purple-50" data-id="element-118">
                {['Libro Digital (+200 recetas)', 'Bonus 1: Menú Semanal Planificado', 'Bonus 2: Guía de Viandas Escolares', 'Bonus 3: Snacks Saludables', 'Bonus 4: Recetas para Fiestas', 'Acceso a comunidad privada'].map((item, i) => <li key={i} className="flex items-center gap-3" data-id="element-119">
                    <Check className="w-5 h-5 text-[#FBBF24] flex-shrink-0" data-id="element-120" />
                    <span className="text-sm font-medium" data-id="element-121">{item}</span>
                  </li>)}
              </ul>

              <Button variant="yellow" size="lg" className="w-full shadow-xl shadow-yellow-500/20" onClick={() => window.location.href = 'https://crianzafeli.online/cart/45751444930750:1'} data-id="element-122">
                ¡QUIERO APROVECHAR!
              </Button>

              <p className="text-center text-xs text-purple-200 mt-4" data-id="element-123">
                Garantía de satisfacción de 7 días
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* --- PREVIEW SECTION --- */}
      <section className="py-20 px-4 bg-white z-10 relative" data-id="element-124">
        <div className="max-w-6xl mx-auto" data-id="element-125">
          <div className="text-center mb-16" data-id="element-126">
            <div className="inline-block bg-orange-100 text-orange-600 px-4 py-1 rounded-full font-bold text-sm mb-4" data-id="element-127">
              SNEAK PEEK
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900" data-id="element-128">
              Mirá un poco lo que vas a encontrar...
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-129">
            {[{
            title: 'Recetas y Snacks',
            img: 'https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&q=80&w=400'
          }, {
            title: 'Bocaditos Felices',
            img: 'https://images.unsplash.com/photo-1512621776951-a57141f2eefd?auto=format&fit=crop&q=80&w=400'
          }, {
            title: 'Comidas Saludables',
            img: 'https://images.unsplash.com/photo-1505253716362-afaea1d3d1af?auto=format&fit=crop&q=80&w=400'
          }, {
            title: 'Snacks Inteligentes',
            img: 'https://images.unsplash.com/photo-1498837167922-ddd27525d352?auto=format&fit=crop&q=80&w=400'
          }].map((item, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.9
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} className="group relative aspect-[3/4] rounded-2xl overflow-hidden shadow-lg cursor-pointer" data-id="element-130">
                <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-131" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end p-6" data-id="element-132">
                  <h3 className="text-white font-bold text-lg" data-id="element-133">{item.title}</h3>
                </div>
              </motion.div>)}
          </div>

          <div className="text-center mt-12" data-id="element-134">
            <Button variant="primary" size="lg" data-id="element-135">
              Ver índice completo
            </Button>
          </div>
        </div>
      </section>
      {/* --- TESTIMONIALS SECTION --- */}
      <section className="py-20 px-4 bg-[#FFF8F0] z-10 relative" data-id="element-136">
        <div className="max-w-6xl mx-auto" data-id="element-137">
          <div className="text-center mb-16" data-id="element-138">
            <div className="flex justify-center gap-1 mb-4" data-id="element-139">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-6 h-6 text-[#FBBF24] fill-current" data-id="element-140" />)}
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900" data-id="element-141">
              Mamás reales, resultados reales
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-142">
            {[{
            name: 'Lucía M.',
            text: 'Me salvó la vida. Literalmente no sabía qué cocinarle a mi nene de 2 años que no quería comer nada verde. Las hamburguesas de espinaca son un éxito.',
            img: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?auto=format&fit=crop&q=80&w=150'
          }, {
            name: 'Carolina R.',
            text: 'Lo que más me gusta es que son ingredientes que tengo en casa. No tengo que salir a comprar cosas raras. Súper práctico.',
            img: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&q=80&w=150'
          }, {
            name: 'Sofía B.',
            text: 'El menú semanal me organizó la vida. Ahora hago las compras una vez y tengo todo resuelto. Vale cada peso.',
            img: 'https://images.unsplash.com/photo-1554151228-14d9def656ec?auto=format&fit=crop&q=80&w=150'
          }].map((testimonial, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-2xl shadow-md border border-gray-100" data-id="element-143">
                <div className="flex items-center gap-4 mb-4" data-id="element-144">
                  <img src={testimonial.img} alt={testimonial.name} className="w-12 h-12 rounded-full object-cover" data-id="element-145" />
                  <div data-id="element-146">
                    <div className="font-bold text-gray-900" data-id="element-147">
                      {testimonial.name}
                    </div>
                    <div className="flex text-[#FBBF24] text-xs" data-id="element-148">
                      {[1, 2, 3, 4, 5].map(star => <Star key={star} className="w-3 h-3 fill-current" data-id="element-149" />)}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 italic" data-id="element-150">"{testimonial.text}"</p>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* --- COMPARISON SECTION --- */}
      <section className="py-20 px-4 bg-white z-10 relative" data-id="element-151">
        <div className="max-w-4xl mx-auto" data-id="element-152">
          <h2 className="text-center text-3xl md:text-4xl font-black text-gray-900 mb-12" data-id="element-153">
            ESTO ES LO QUE{' '}
            <span className="text-[#EF4444] underline decoration-4 decoration-[#FBBF24]" data-id="element-154">
              CAMBIA
            </span>
            <br data-id="element-155" />
            CUANDO USÁS LA GUÍA
          </h2>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-156">
            <div className="bg-red-50 p-8 rounded-3xl border border-red-100" data-id="element-157">
              <h3 className="text-xl font-bold text-red-600 mb-6 flex items-center" data-id="element-158">
                <span className="bg-red-200 p-2 rounded-lg mr-3" data-id="element-159">😫</span> ANTES
              </h3>
              <ul className="space-y-4" data-id="element-160">
                {['Estrés diario pensando qué cocinar', 'Tu hijo rechaza la comida', 'Gastás de más en delivery', 'Comidas aburridas y repetitivas'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-700" data-id="element-161">
                    <span className="text-red-400 mt-1" data-id="element-162">✖</span>
                    {item}
                  </li>)}
              </ul>
            </div>

            <div className="bg-green-50 p-8 rounded-3xl border-2 border-green-200 shadow-lg relative overflow-hidden" data-id="element-163">
              <div className="absolute top-0 right-0 bg-[#10B981] text-white text-xs font-bold px-3 py-1 rounded-bl-xl" data-id="element-164">
                TU NUEVA VIDA
              </div>
              <h3 className="text-xl font-bold text-green-700 mb-6 flex items-center" data-id="element-165">
                <span className="bg-green-200 p-2 rounded-lg mr-3" data-id="element-166">😍</span>{' '}
                DESPUÉS
              </h3>
              <ul className="space-y-4" data-id="element-167">
                {['Menú organizado y compras eficientes', 'Tu hijo prueba nuevos sabores', 'Ahorrás dinero cocinando en casa', 'Disfrutás el momento de la comida'].map((item, i) => <li key={i} className="flex items-start gap-3 text-gray-800 font-medium" data-id="element-168">
                    <Check className="w-5 h-5 text-[#10B981] mt-0.5" data-id="element-169" />
                    {item}
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* --- COUNTDOWN SECTION --- */}
      <section className="py-16 px-4 bg-[#FFF8F0] z-10 relative" data-id="element-170">
        <div className="max-w-4xl mx-auto text-center" data-id="element-171">
          <h2 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-172">
            No dejes pasar esta oportunidad única
          </h2>
          <CountdownTimer data-id="element-173" />
          <div className="mt-8" data-id="element-174">
            <Button size="xl" variant="primary" className="shadow-xl shadow-green-500/30 animate-pulse" onClick={() => window.location.href = 'https://crianzafeli.online/cart/45751444930750:1'} data-id="element-175">
              QUIERO ACCEDER AHORA
            </Button>
          </div>
        </div>
      </section>
      {/* --- FAQ SECTION --- */}
      <section className="py-20 px-4 bg-white z-10 relative" data-id="element-176">
        <div className="max-w-3xl mx-auto" data-id="element-177">
          <h2 className="text-3xl font-black text-center text-gray-900 mb-12" data-id="element-178">
            PREGUNTAS FRECUENTES
          </h2>

          <Accordion items={[{
          question: '¿Cómo recibo el libro?',
          answer: 'El acceso es inmediato. Una vez realizado el pago, te llega un mail con el link de descarga a tu correo electrónico. Podés descargarlo en tu celular, tablet o computadora.'
        }, {
          question: '¿Sirve para niños de qué edad?',
          answer: 'Las recetas están pensadas para toda la familia, pero especialmente adaptadas para niños a partir de los 6 meses (con adaptaciones) hasta los 10 años.'
        }, {
          question: '¿Necesito ingredientes raros?',
          answer: "¡Para nada! La filosofía del libro es 'cocina real para madres reales'. Usamos ingredientes que conseguís en cualquier verdulería o supermercado de barrio."
        }, {
          question: '¿Qué pasa si no me gusta?',
          answer: 'Tenés 7 días de garantía. Si sentís que el libro no es para vos, nos escribís y te devolvemos el 100% de tu dinero. Sin preguntas.'
        }]} data-id="element-179" />
        </div>
      </section>
      {/* --- FINAL CTA SECTION --- */}
      <section className="py-20 px-4 bg-gradient-to-b from-[#FFF8F0] to-white z-10 relative" data-id="element-180">
        <div className="max-w-5xl mx-auto bg-white rounded-[2.5rem] shadow-2xl overflow-hidden border border-gray-100" data-id="element-181">
          <div className="grid md:grid-cols-2" data-id="element-182">
            <div className="bg-[#10B981] p-12 flex items-center justify-center relative overflow-hidden" data-id="element-183">
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/food.png')] opacity-10" data-id="element-184"></div>
              <img src="https://cdn.magicpatterns.com/uploads/fiixULfCbFeXEPK4fxENYi/PHOTO-2025-12-17-10-47-19-removebg-preview.webp" alt="Libro Final" className="relative z-10 rounded-lg shadow-2xl transform rotate-3 hover:rotate-0 transition-all duration-500 w-3/4" data-id="element-185" />
            </div>

            <div className="p-6 md:p-12 flex flex-col justify-center text-center md:text-left" data-id="element-186">
              <h2 className="text-2xl md:text-3xl font-black text-gray-900 mb-4" data-id="element-187">
                ¡Empezá hoy mismo!
              </h2>
              <p className="text-gray-600 mb-8" data-id="element-188">
                Llevate el libro digital + los 4 bonus de regalo + acceso a la
                comunidad.
              </p>

              <div className="mb-8" data-id="element-189">
                <div className="flex items-baseline gap-3 justify-center md:justify-start" data-id="element-190">
                  <span className="text-4xl md:text-5xl font-black text-[#EF4444]" data-id="element-191">
                    $16.990
                  </span>
                  <span className="text-lg md:text-xl text-gray-400 line-through" data-id="element-192">
                    $42.500
                  </span>
                </div>
                <p className="text-sm text-green-600 font-bold mt-2" data-id="element-193">
                  ¡Ahorrás un 60% HOY!
                </p>
              </div>

              <Button variant="yellow" size="xl" className="w-full mb-6" onClick={() => window.location.href = 'https://crianzafeli.online/cart/45751444930750:1'} data-id="element-194">
                QUIERO MI LIBRO YA ➔
              </Button>

              <div className="flex items-center justify-center gap-4 text-gray-400 grayscale opacity-70" data-id="element-195">
                <CreditCard className="w-8 h-8" data-id="element-196" />
                <span className="text-xs" data-id="element-197">Pagos seguros y encriptados</span>
              </div>
            </div>
          </div>
        </div>

        <footer className="text-center mt-16 text-gray-400 text-sm" data-id="element-198">
          <p data-id="element-199">© 2024 ¡Mi Hijo Sí Come! - Todos los derechos reservados.</p>
          <div className="flex justify-center gap-4 mt-4" data-id="element-200">
            <a href="#" className="hover:text-gray-600" data-id="element-201">
              Términos y Condiciones
            </a>
            <a href="#" className="hover:text-gray-600" data-id="element-202">
              Política de Privacidad
            </a>
          </div>
        </footer>
      </section>
    </div>;
}
// Helper icons
function ClockIcon({
  className
}: {
  className?: string;
}) {
  return <>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-203">
        <circle cx="12" cy="12" r="10" data-id="element-204" />
        <polyline points="12 6 12 12 16 14" data-id="element-205" />
      </svg>
    </>;
}
function GiftIcon({
  className
}: {
  className?: string;
}) {
  return <>
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-206">
        <polyline points="20 12 20 22 4 22 4 12" data-id="element-207" />
        <rect x="2" y="7" width="20" height="5" data-id="element-208" />
        <line x1="12" y1="22" x2="12" y2="7" data-id="element-209" />
        <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z" data-id="element-210" />
        <path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z" data-id="element-211" />
      </svg>
    </>;
}